import { getHomeUrl } from '@/utils/cp-context';
import type { Breadcrumb } from '@ps-refarch-ux/neon';

export enum CONFIGURATIONS_ID {
  Breadcrumbs = 'configurations-breadcrumbs',
  Tabs = 'configurations-tabs',
  ConfigurationsCancel = 'configurations-approvals-cancel',
  ConfigurationsSave = 'configurations-approvals-save',
  LeaveModal = 'configurations-leave-without-saving-modal',
  GradeCreditAlertsPanel = 'configurations-grade-credit-alerts-panel',
  GradeCreditAlertsPanelClose = 'configurations-grade-credit-alerts-panel-close',
  GradeCreditAlertsPanelCancel = 'configurations-grade-credit-alerts-panel-cancel',
  GradeCreditAlertsPanelSave = 'configurations-grade-credit-alerts-panel-save',
  RemoveCreditAlertsModal = 'configurations-remove-credit-alerts-modal',
  CreditDeficiencyPanel = 'configurations-credit-deficiency-panel',
  CreditDeficiencyPanelClose = 'configurations-credit-deficiency-panel-close',
  CreditDeficiencyPanelCancel = 'configurations-credit-deficiency-panel-cancel',
  CreditDeficiencyPanelSave = 'configurations-credit-deficiency-panel-save',
  RemoveCreditDeficiencyModal = 'configurations-remove-credit-deficiency-modal',
}

export enum CONFIGURATIONS_TEXT {
  BreadcrumbHome = 'Home',
  Title = 'Course planner configuration',
  Description = 'Manage course planner settings and preferences.',
}

export const CONFIGURATIONS_TAB_IDS = {
  Approvals: 'approvals',
  AcademicRules: 'academic-rules',
  StudentExperience: 'student-experience',
} as const;

export type ConfigurationsTabId =
  (typeof CONFIGURATIONS_TAB_IDS)[keyof typeof CONFIGURATIONS_TAB_IDS];

export const getConfigurationsBreadcrumbs: Breadcrumb[] = [
  { text: CONFIGURATIONS_TEXT.BreadcrumbHome, url: getHomeUrl() },
  { text: CONFIGURATIONS_TEXT.Title, url: '#' },
];

/** Approvals tab: Approval Requirements (values match API approvalRequirement) */
export const APPROVAL_REQUIREMENTS_LABEL = 'Approval requirements';
export const APPROVAL_REQUIREMENT_VALUES = {
  SchoolApproval: 'SCHOOL_APPROVAL_REQUIRED',
  SchoolAndGuardian: 'SCHOOL_AND_PARENT_APPROVAL_REQUIRED',
  NoApproval: 'NO_APPROVAL_REQUIRED',
} as const;
export const APPROVAL_REQUIREMENT_OPTIONS = [
  {
    id: 'opt-school',
    labelText: 'School approval',
    value: APPROVAL_REQUIREMENT_VALUES.SchoolApproval,
  },
  {
    id: 'opt-school-guardian',
    labelText: 'School and guardian approval',
    value: APPROVAL_REQUIREMENT_VALUES.SchoolAndGuardian,
  },
  { id: 'opt-none', labelText: 'No approval', value: APPROVAL_REQUIREMENT_VALUES.NoApproval },
] as const;

/** Approvals tab: Student Submission Rules (values match API planApprovalOption) */
export const STUDENT_SUBMISSION_RULES_LABEL = 'Student submission rules';
export const STUDENT_SUBMISSION_VALUES = {
  WithAlerts: 'SUBMIT_REGARDLESS_ALERTS',
  NextYearNoAlerts: 'CHECK_NEXT_ACADEMIC_YEAR_ALERTS',
  NoAlerts: 'CHECK_ALL_ALERTS',
} as const;
export const STUDENT_SUBMISSION_OPTIONS = [
  {
    id: 'sub-with-alerts',
    labelText: 'Allow plan submission with alerts',
    value: STUDENT_SUBMISSION_VALUES.WithAlerts,
  },
  {
    id: 'sub-next-year',
    labelText: 'Allow plan submission if next year has no alerts',
    value: STUDENT_SUBMISSION_VALUES.NextYearNoAlerts,
  },
  {
    id: 'sub-no-alerts',
    labelText: 'Allow plan submission only if no alerts exist',
    value: STUDENT_SUBMISSION_VALUES.NoAlerts,
  },
] as const;

/** Approvals tab: Staff Approval Rules (student plan manager only) */
export const STAFF_APPROVAL_RULES_LABEL = 'Staff approval rules (student course plan manager only)';
export const STAFF_APPROVAL_VALUES = {
  ApproveAlways: 'APPROVE_REGARDLESS',
  ApproveAfterSubmission: 'APPROVE_AFTER_SUBMISSION',
} as const;
export const STAFF_APPROVAL_OPTIONS = [
  {
    id: 'staff-always',
    labelText:
      'Staff can approve plans, even if the plan has alerts, has not been submitted by the student, or has not been approved by the guardian.',
    value: STAFF_APPROVAL_VALUES.ApproveAlways,
  },
  {
    id: 'staff-after',
    labelText:
      'Staff can approve plans only after students have submitted them and guardian approval is complete, if required.',
    value: STAFF_APPROVAL_VALUES.ApproveAfterSubmission,
  },
] as const;

/** Configurations page-level action bar (Cancel/Save apply across all tabs) */
export const CONFIGURATIONS_BUTTON_CANCEL = 'Cancel';
export const CONFIGURATIONS_BUTTON_SAVE = 'Save';

/** Leave without saving modal */
export const LEAVE_MODAL_TITLE = 'Leave without saving?';
export const LEAVE_MODAL_MESSAGE = 'You will lose unsaved changes.';
export const LEAVE_MODAL_LEAVE = 'Leave without saving';
export const LEAVE_MODAL_CONTINUE = 'Continue editing';

/** Toast after save */
export const CONFIGURATIONS_SAVE_TOAST_HEADING = 'Configurations saved';
/** Toast when save fails */
export const CONFIGURATIONS_SAVE_ERROR_HEADING = 'Failed to save configurations';

/** Approvals tab form state (shared for controlled component) */
export interface ApprovalsFormState {
  approvalRequirement: string;
  studentSubmission: string;
  staffApproval: string;
}

/** Academic rules tab: Credit recovery (values match API planFeatureOption) */
export const CREDIT_RECOVERY_LABEL = 'Credit recovery';
export const CREDIT_RECOVERY_DESCRIPTION =
  'Students can recover credits for courses where they earned 0 credits.';
/** Two bullet points under Credit recovery */
export const CREDIT_RECOVERY_BULLETS = [
  'If a student receives 0 credits for an attempted course, they can add another course from the same requirement to recover that credit.',
  'If specific recovery courses are listed in the Credit Recovery field in the course catalog, students can select those courses.',
] as const;
export const CREDIT_RECOVERY_VALUES = {
  Prompt: 'STUDENT_CAN_SELECT_RECOVERY_COURSE',
  DoNotPrompt: 'STUDENT_CAN_NOT_SELECT_RECOVERY_COURSE',
} as const;

/** Matches legacy c-p-ui: prompt recovery UI unless district chose "do not prompt". */
export function isCreditRecoveryPromptEnabled(planFeatureOption?: string): boolean {
  if (!planFeatureOption) {
    return true;
  }
  return planFeatureOption === CREDIT_RECOVERY_VALUES.Prompt;
}

export const CREDIT_RECOVERY_OPTIONS = [
  {
    id: 'credit-recovery-prompt',
    labelText: 'Prompt students to select a recovery course when they receive 0 credits.',
    value: CREDIT_RECOVERY_VALUES.Prompt,
  },
  {
    id: 'credit-recovery-no-prompt',
    labelText: 'Do not prompt students to select recovery courses.',
    value: CREDIT_RECOVERY_VALUES.DoNotPrompt,
  },
] as const;

/** Academic rules tab: Grade-specific credit alerts */
export const GRADE_CREDIT_ALERTS_LABEL =
  'Grade-specific credit alerts (only displayed to students when creating a course plan)';
export const GRADE_CREDIT_ALERTS_DESCRIPTION =
  "Set the minimum and maximum credits for each grade level. When a student's planned credits fall outside this range, an alert will be displayed when they check for alerts.";
export const GRADE_CREDIT_ALERTS_NOTE =
  "If a grade level doesn't require a minimum or maximum, leave those fields blank.";
export const GRADE_CREDIT_ALERTS_BUTTON = 'Set up credit alerts';
export const GRADE_CREDIT_ALERTS_EDIT_BUTTON = 'Edit credit alerts';
export const GRADE_CREDIT_ALERTS_REMOVE_BUTTON = 'Remove credit alerts';

/** Remove credit alerts confirmation modal */
export const REMOVE_CREDIT_ALERTS_MODAL_TITLE = 'Remove credit alerts?';
export const REMOVE_CREDIT_ALERTS_MODAL_BODY_1 =
  'Removing credit alert ranges will stop alerts from appearing when planned credits fall outside the minimum or maximum for a grade level.';
export const REMOVE_CREDIT_ALERTS_MODAL_BODY_2 =
  'If plan of study-specific alerts are set up, they will continue to be displayed for students.';
export const REMOVE_CREDIT_ALERTS_MODAL_CANCEL = 'Cancel';
export const REMOVE_CREDIT_ALERTS_MODAL_CONFIRM = 'Remove credit alerts';

/** Grade credit alerts right-side panel */
export const GRADE_CREDIT_ALERTS_PANEL_TITLE =
  'Grade specific credit alerts (only displayed to students when creating a course plan)';
export const GRADE_CREDIT_ALERTS_PANEL_COL_GRADE = 'Grade';
export const GRADE_CREDIT_ALERTS_PANEL_COL_MIN = 'Minimum credits';
export const GRADE_CREDIT_ALERTS_PANEL_COL_MAX = 'Maximum credits';
export const GRADE_CREDIT_ALERTS_PANEL_CANCEL = 'Cancel';
export const GRADE_CREDIT_ALERTS_PANEL_SAVE = 'Save';
export const GRADE_CREDIT_ALERTS_PANEL_VALIDATION_MIN_MAX = 'Minimum cannot exceed maximum';
export const GRADE_CREDIT_ALERTS_PLACEHOLDER_MIN = 'No minimum';
export const GRADE_CREDIT_ALERTS_PLACEHOLDER_MAX = 'No maximum';
/** Grades 6 through 12 inclusive for panel table (edit and initial set up). Must include 11 and 12. */
export const GRADE_CREDIT_ALERTS_GRADES = [6, 7, 8, 9, 10, 11, 12] as const;

/** Academic rules tab: Credit deficiency */
export const CREDIT_DEFICIENCY_LABEL =
  'Credit deficiency (only displayed to staff in Student Course Plan Manager)';
export const CREDIT_DEFICIENCY_DESCRIPTION =
  "Set the cumulative credits required for each grade level to keep students on track for graduation. If a student has fewer credits than the previous grade's requirement, they are marked as credit deficient. Credits earned in the current grade count only after the grade is complete.";
export const CREDIT_DEFICIENCY_NOTE =
  'Leave fields blank if no minimum is required. Credit deficiency status appears only in the';
export const CREDIT_DEFICIENCY_NOTE_BOLD = 'Student course plan manager';
export const CREDIT_DEFICIENCY_NOTE_SUFFIX = ' and is not shown to students.';
export const CREDIT_DEFICIENCY_BUTTON = 'Set up credit deficiency';
export const CREDIT_DEFICIENCY_EDIT_BUTTON = 'Edit credit deficiency';
export const CREDIT_DEFICIENCY_REMOVE_BUTTON = 'Remove credit deficiency';

/** Remove credit deficiency confirmation modal */
export const REMOVE_CREDIT_DEFICIENCY_MODAL_TITLE = 'Remove credit deficiency?';
export const REMOVE_CREDIT_DEFICIENCY_MODAL_BODY =
  'Removing grade-level cumulative credit requirements will stop identifying students as credit deficient in the';
export const REMOVE_CREDIT_DEFICIENCY_MODAL_BODY_BOLD = 'Student course plan manager';
export const REMOVE_CREDIT_DEFICIENCY_MODAL_BODY_SUFFIX = '.';
export const REMOVE_CREDIT_DEFICIENCY_MODAL_CANCEL = 'Cancel';
export const REMOVE_CREDIT_DEFICIENCY_MODAL_CONFIRM = 'Remove credit deficiency';

/** Credit deficiency right-side panel */
export const CREDIT_DEFICIENCY_PANEL_TITLE =
  'Credit deficiency (only displayed to staff in Student Course Plan Manager)';
export const CREDIT_DEFICIENCY_PANEL_COL_GRADE = 'Grade';
export const CREDIT_DEFICIENCY_PANEL_COL_MIN_CUMULATIVE = 'Minimum cumulative credits earned';
export const CREDIT_DEFICIENCY_PANEL_CANCEL = 'Cancel';
export const CREDIT_DEFICIENCY_PANEL_SAVE = 'Save';
export const CREDIT_DEFICIENCY_GRADES = [6, 7, 8, 9, 10, 11, 12] as const;
export const CREDIT_DEFICIENCY_NO_MINIMUM = 'No minimum';

/** Student Experience tab — Configure labels */
export const CONFIGURE_LABELS_LABEL = 'Configure labels';
export const CONFIGURE_LABELS_DESCRIPTION =
  'Customize terminology to match what your school or district uses. These changes apply only within Course Planner. Fields left blank will use the default term.';
export const CONFIGURE_LABELS_HELPER = 'Changes the label everywhere in Course Planner';
export const CONFIGURE_LABELS_MAX_LENGTH = 17;
export const CONFIGURE_LABELS_CLUSTER_DEFAULT = 'Area';
export const CONFIGURE_LABELS_PATHWAY_DEFAULT = 'Specialization';

/** Student Experience tab — Term settings */
export const TERM_SETTINGS_LABEL = 'Term settings';
export const TERM_SETTINGS_DESCRIPTION =
  'Set whether students can choose a term when selecting courses. Then define the terms students can select.';
export const TERM_SETTINGS_ENABLE_LABEL = 'Enable term setting for students.';
export const TERM_SELECTION_LIST_LABEL = 'Term selection list';
export const TERM_SELECTION_MAX_COUNT = 6;
export const TERM_SELECTION_MAX_LABEL = `Max of ${TERM_SELECTION_MAX_COUNT}`;
export const TERM_SELECTION_ADD_BUTTON = 'Add term';
export const TERM_SELECTION_MAX_MESSAGE = `A maximum of ${TERM_SELECTION_MAX_COUNT} terms can be added.`;
/** Shown when Save is blocked because term setting is on but a term row has an empty name */
export const TERM_NAMES_REQUIRED_TOAST_HEADING = 'Enter a name for every term before saving.';
export const TERM_NAMES_REQUIRED_INLINE_MESSAGE =
  'Each term must have a name. Fill in all term names or remove extra rows.';

/** Student Experience tab — School selection */
export const SCHOOL_SELECTION_LABEL = 'School selection';
export const SCHOOL_SELECTION_DESCRIPTION =
  'Allow school selection so student can plan courses outside their current school.';
export const SCHOOL_SELECTION_VALUES = {
  DoNotAllow: 'NO_SCHOOL_SELECTION',
  AllStudents: 'ALL_STUDENTS',
  SpecificGrades: 'SPECIFIC_GRADES',
} as const;
export const SCHOOL_SELECTION_OPTIONS = [
  {
    id: 'school-none',
    labelText: 'Do not allow school selection.',
    value: SCHOOL_SELECTION_VALUES.DoNotAllow,
  },
  {
    id: 'school-all',
    labelText: 'Allow school selection for all students.',
    value: SCHOOL_SELECTION_VALUES.AllStudents,
  },
  {
    id: 'school-grades',
    labelText: 'Allow school selection for specific grades.',
    value: SCHOOL_SELECTION_VALUES.SpecificGrades,
  },
] as const;

/** Grades shown when “Allow school selection for specific grades” is selected */
export const SCHOOL_SELECTION_GRADES_LIST = [6, 7, 8, 9, 10, 11, 12] as const;
export const SCHOOL_SELECTION_GRADES_LABEL = 'Grades';
export const SCHOOL_SELECTION_GRADES_REQUIRED_TOAST_HEADING =
  'Select at least one grade before saving.';
export const SCHOOL_SELECTION_GRADES_REQUIRED_INLINE_MESSAGE =
  'Choose one or more grades when allowing school selection for specific grades.';

/** Student Experience tab — Additional resources (schoolClusterGuide) */
export const ADDITIONAL_RESOURCES_LABEL = 'Additional resources';
export const ADDITIONAL_RESOURCES_DESCRIPTION =
  "Add links to websites and upload a document for students to download. These resources will be displayed on the student's Manage Plan page.";
export const ADDITIONAL_RESOURCES_SHOW_LABEL = 'Show to students';

export const ADDITIONAL_RESOURCES_LINKS_LABEL = 'Links';
export const ADDITIONAL_RESOURCES_MAX_LINKS = 3;
export const ADDITIONAL_RESOURCES_LINKS_MAX_LABEL = `Max of ${ADDITIONAL_RESOURCES_MAX_LINKS}`;
export const ADDITIONAL_RESOURCES_ADD_LINK = '+ Add link';
export const ADDITIONAL_RESOURCES_LINKS_MAX_MESSAGE = `A maximum of ${ADDITIONAL_RESOURCES_MAX_LINKS} custom links can be added.`;
/** Shown when a link row is missing a URL */
export const LINK_NAME_AND_URL_REQUIRED_INLINE_MESSAGE = 'URL field is required';

/** Shown when Save is blocked because Show to students is on but no link or document is provided */
export const ADDITIONAL_RESOURCES_CONTENT_REQUIRED_INLINE_MESSAGE =
  'When "Show to students" is enabled, at least one link or a document must be provided.';

export const ADDITIONAL_RESOURCES_DOCUMENT_LABEL = 'Document';
export const ADDITIONAL_RESOURCES_DOC_NAME_LABEL = 'Document name';
export const ADDITIONAL_RESOURCES_DOC_NAME_PLACEHOLDER = 'Document from your school';
export const ADDITIONAL_RESOURCES_DOC_NAME_MAX = 55;
export const ADDITIONAL_RESOURCES_UPLOAD_LABEL = 'Upload document';
export const ADDITIONAL_RESOURCES_UPLOAD_HELPER = 'The file size cannot exceed 500KB';
export const ADDITIONAL_RESOURCES_FILE_SIZE_LIMIT_KB = 500;
export const ADDITIONAL_RESOURCES_FILE_TOO_LARGE_MESSAGE =
  'File size exceeds 500KB. Please upload a smaller file.';
export const ADDITIONAL_RESOURCES_UPLOAD_BUTTON = 'Upload file';
export const ADDITIONAL_RESOURCES_REPLACE_BUTTON = 'Replace file';

export interface AdditionalResourcesLink {
  name: string;
  url: string;
}

export interface StudentExperienceFormState {
  clusterLabel: string;
  pathwayLabel: string;
  enableTermSetting: boolean;
  terms: string[];
  schoolSelection: string;
  /** Used when schoolSelection is SPECIFIC_GRADES; subset of SCHOOL_SELECTION_GRADES_LIST */
  schoolSelectionGrades: number[];
  showToStudents: boolean;
  links: AdditionalResourcesLink[];
  documentName: string;
  /** Filename returned by the server (or the name of a previously-uploaded file). */
  documentFileName: string;
  /** The actual File object selected by the user — present only when a new file has been
   *  chosen but not yet uploaded. Cleared after a successful upload on Save. */
  documentFile?: File;
}

/** When term setting is enabled, every listed term must have a non-empty name (after trim). */
export function areStudentExperienceTermsValid(
  state: Pick<StudentExperienceFormState, 'enableTermSetting' | 'terms'>
): boolean {
  if (!state.enableTermSetting) {
    return true;
  }
  if (state.terms.length === 0) {
    return false;
  }
  return state.terms.every((t) => t.trim() !== '');
}

/** Every link row must have a URL non-empty (after trim). Name is optional. Empty list is valid. */
export function areAdditionalResourcesLinksValid(links: AdditionalResourcesLink[]): boolean {
  return links.every((l) => l.url.trim() !== '');
}

const ALLOWED_SCHOOL_SELECTION_GRADES = new Set<number>(SCHOOL_SELECTION_GRADES_LIST);

/** When school selection is “specific grades”, at least one valid grade (6–12) must be chosen. */
export function areSchoolSelectionGradesValid(
  state: Pick<StudentExperienceFormState, 'schoolSelection' | 'schoolSelectionGrades'>
): boolean {
  if (state.schoolSelection !== SCHOOL_SELECTION_VALUES.SpecificGrades) {
    return true;
  }
  const grades = state.schoolSelectionGrades.filter((g) => ALLOWED_SCHOOL_SELECTION_GRADES.has(g));
  return grades.length > 0;
}

/**
 * When "Show to students" is checked, at least one link (with a valid name+URL)
 * OR an uploaded document (non-empty documentFileName) must be present.
 */
export function isAdditionalResourcesContentValid(
  state: Pick<StudentExperienceFormState, 'showToStudents' | 'links' | 'documentFileName'>
): boolean {
  if (!state.showToStudents) {
    return true;
  }
  const hasValidLink = state.links.some((l) => l.url.trim() !== '');
  const hasDocument = state.documentFileName.trim() !== '';
  return hasValidLink || hasDocument;
}
